import { useEffect, useState } from 'react';
import { Button, Form, Input, Message, Modal, Select, Space, Switch, Tooltip, Typography } from '@arco-design/web-react';
import { IconThunderbolt } from '@arco-design/web-react/icon';
import CopyText from './CopyText';
import DynamicTicketFields from './DynamicTicketFields';
import FieldTip from './FieldTip';
import FormModal, { FORM_MODAL_WIDTH } from './FormModal';
import {
  createTicket,
  prefillTicket,
  TICKET_TYPE_OPTIONS,
  listEnabledTicketFields,
  listEnabledTicketTemplates,
  parseTicketTemplateDefaults,
  ticketTemplateFieldKeys,
  getVocabTree,
  type PrefillDraft,
  type Ticket,
  type TicketField,
  type TicketTemplate,
  type VocabTreeNode,
} from '../api/ticket';
import { getCustomer, listCustomers, type Customer } from '../api/crm';
import { listSelectableTemplates, type WorkflowTemplate } from '../api/workflow';
import { listUserOptions, type UserOption } from '../api/system';
import type { Cdr } from '../api/cdr';
import { maskPhone } from '../utils/format';

const TYPE_OPTIONS = TICKET_TYPE_OPTIONS;

const PRIORITY_OPTIONS = [
  { label: '低', value: 'low' },
  { label: '中', value: 'medium' },
  { label: '高', value: 'high' },
  { label: '紧急', value: 'urgent' },
];

const PHONE_FIELD_KEYS = new Set(['phone', 'mobile', 'mobile_phone', 'cellphone', 'contact_phone', 'customer_phone']);

/** 判定扩展字段语义为手机号（key 命中常见号码键 或 label 含 手机/联系电话/联系方式） */
function isPhoneField(f: TicketField): boolean {
  if (PHONE_FIELD_KEYS.has(String(f.field_key || '').toLowerCase().trim())) return true;
  const label = f.label || '';
  return (
    label.includes('手机') ||
    label.includes('手机号') ||
    label.includes('联系电话') ||
    label.includes('联系方式')
  );
}

export interface TicketCreateModalProps {
  visible: boolean;
  onClose: (created?: Ticket) => void;
  /** 关联通话 CDR（发起来源：小结弹窗 / 呼叫记录详情） */
  cdr?: Cdr | null;
  /** 关联在线会话（发起来源：工作台 Tab 行「建工单」/ 会话头部「新建工单」） */
  chatSessionId?: number;
  /** 关联在途通话 uuid（发起来源：工作台「通话」Tab 建单，CDR 尚未落库） */
  uuid?: string;
  /** 默认勾选「需回访」（跟随服务小结的 follow_up） */
  defaultFollowUp?: boolean;
  /** 预填客户 id（会话/通话已识别身份时带入，缺省走客户搜索） */
  defaultCustomerId?: number;
  /** 通话语境客户手机号（在途通话建单传入；未匹配到客户时提交由后端按号查/建客户） */
  defaultCustomerPhone?: string;
}

/**
 * 发起工单弹窗（挂断小结 / 呼叫记录详情 / 坐席工作台共用）：
 * 关联当前通话/会话客户自动带入，支持 AI 预填 + 工单模板选择（模板限定动态字段）。
 */
export default function TicketCreateModal({
  visible,
  onClose,
  cdr,
  chatSessionId,
  uuid,
  defaultFollowUp,
  defaultCustomerId,
  defaultCustomerPhone,
}: TicketCreateModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowTemplate[]>([]);
  // 处理人候选：留空则由后端按派单规则自动指派（见「处理人」表单项说明）
  const [users, setUsers] = useState<UserOption[]>([]);
  // v1.16 扩展字段：启用字段定义（坐席/任意用户可读）+ 分类树（cascade 用）
  const [ticketFields, setTicketFields] = useState<TicketField[]>([]);
  const [categoryTree, setCategoryTree] = useState<VocabTreeNode[]>([]);
  // 工单创建模板（快捷建单）：选择后限定/预填动态字段
  const [ticketTemplates, setTicketTemplates] = useState<TicketTemplate[]>([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState<number | undefined>();
  // 模板候选是否已加载完（加载中不得放行 AI 预填：此刻 ticketTemplates 尚为空数组，
  // 会把「还没到货」误判成「没有模板」，见 prefillBlocked 与 doPrefill 的门禁）
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // 通话语境客户号码：优先显式传入（工作台在途通话），回退 CDR 主被叫（小结/呼叫记录详情，兼容旧 CDR 未建档）
  const cdrPhone = cdr?.id ? (cdr.direction === 'inbound' ? cdr.caller || '' : cdr.callee || '') : '';
  const contextPhone = defaultCustomerPhone || cdrPhone || '';

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    form.setFieldsValue({
      type: 'consult',
      priority: 'medium',
      followUp: !!defaultFollowUp,
      customerId: (cdr as any)?.customer_id ?? defaultCustomerId ?? undefined,
    });
    setAppliedTemplateId(undefined);
    setTemplatesLoaded(false);
    initCustomers();
    // 加载可用的工单审核流程（发起工单统一走审核流程，不再直接指派坐席）
    listSelectableTemplates('ticket')
      .then(setWorkflows)
      .catch(() => setWorkflows([]));
    // 处理人候选：默认留空，由后端按派单规则（默认处理人 → 负责技能组内负载最轻 → 创建人）自动指派
    listUserOptions()
      .then(setUsers)
      .catch(() => setUsers([]));
    // v1.16 扩展字段：统一组件渲染（启用字段，供坐席填写/修改 AI 预填值）
    listEnabledTicketFields()
      .then(setTicketFields)
      .catch(() => setTicketFields([]));
    getVocabTree()
      .then(setCategoryTree)
      .catch(() => setCategoryTree([]));
    // 工单创建模板（快捷建单）：坐席选择后限定并预填动态字段
    listEnabledTicketTemplates()
      .then(setTicketTemplates)
      .catch(() => setTicketTemplates([]))
      .finally(() => setTemplatesLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, cdr, defaultFollowUp]);

  // 语境手机号 → 掩码预填手机号类扩展字段（input/textarea，仅空值补填；触发点：弹窗打开/字段加载完成）
  useEffect(() => {
    if (!visible) return;
    const phone = contextPhone.trim();
    if (!phone) return;
    const f = ticketFields.find((x) => (x.type === 'input' || x.type === 'textarea') && isPhoneField(x));
    if (!f) return;
    const cur = form.getFieldValue(`df_${f.field_key}`);
    if (cur === undefined || cur === null || String(cur).trim() === '') {
      form.setFieldValue(`df_${f.field_key}`, maskPhone(phone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, contextPhone, ticketFields]);

  // 无条件加载最近客户；CDR 已关联客户时单独拉取并置顶，保证预填值始终有对应选项
  const initCustomers = async () => {
    setCustomers([]);
    searchCustomers();
    const cid = (cdr as any)?.customer_id;
    // 通话语境手机号 → 精确匹配自动带出客户（未命中保留为空，提交时后端按号查/建）
    if (!cid && !defaultCustomerId && contextPhone) {
      try {
        const { list } = await listCustomers({ phone: contextPhone, page: 1, pageSize: 5 });
        if (list?.length) {
          const hit = list.find((c) => c.phone === contextPhone) || list[0];
          form.setFieldValue('customerId', hit.id);
          setCustomers((prev) => (prev.some((x) => x.id === hit.id) ? prev : [hit, ...prev]));
        }
      } catch { /* noop */ }
      return;
    }
    if (!cid) return;
    try {
      const c = await getCustomer(cid);
      if (c?.id) {
        setCustomers((prev) => (prev.some((x) => x.id === c.id) ? prev : [c, ...prev]));
      }
    } catch { /* 客户可能已被删除，忽略 */ }
  };

  const searchCustomers = async (keyword?: string) => {
    try {
      const { list } = await listCustomers({ keyword: keyword || undefined, page: 1, pageSize: 20 });
      setCustomers(list);
    } catch { /* noop */ }
  };

  // 应用工单创建模板：预填基础字段 + 动态字段默认值，并按模板限定动态字段
  const applyTicketTemplate = (tpl?: TicketTemplate) => {
    setAppliedTemplateId(tpl?.id);
    if (!tpl) return;
    const patch: Record<string, any> = {};
    if (tpl.type) patch.type = tpl.type;
    if (tpl.priority) patch.priority = tpl.priority;
    if (tpl.title) patch.title = tpl.title;
    if (tpl.description) patch.description = tpl.description;
    if (tpl.workflow_template_id) patch.workflowTemplateId = tpl.workflow_template_id;
    patch.followUp = !!tpl.follow_up;
    form.setFieldsValue(patch);
    const defs = parseTicketTemplateDefaults(tpl.field_defaults);
    Object.keys(defs).forEach((k) => form.setFieldValue(`df_${k}`, defs[k]));
  };

  // 可见动态字段：选了模板 → 仅模板所需字段（无所需字段则隐藏整区）；否则全量启用字段
  const templateKeys = ticketTemplateFieldKeys(ticketTemplates.find((t) => t.id === appliedTemplateId));
  const visibleFields = appliedTemplateId
    ? ticketFields.filter((f) => templateKeys.includes(f.field_key))
    : ticketFields;

  // AI 预填门禁：模板候选就绪 + 有模板就必须先选一个（顺序 = 模板定字段范围 → AI 在范围内抽值）。
  // 无启用模板时不拦——那时没有可选模板，禁用按钮只会把 AI 预填一并锁死。
  const prefillBlocked = !templatesLoaded || (ticketTemplates.length > 0 && !appliedTemplateId);
  const prefillTip = !templatesLoaded
    ? '工单模板加载中，请稍候'
    : '请先选择「应用工单模板」，再点击 AI 预填';

  const doPrefill = async () => {
    // 门禁：须先选模板再 AI 预填（顺序是「模板定字段范围 → AI 在范围内抽值」；详见按钮旁提示）。
    // 只做 UI 禁用不够——加载竞态与后续改版都可能让按钮在无模板时仍可点，故在此复核。
    if (!cdr?.id && !chatSessionId) return;
    if (!templatesLoaded) {
      Message.warning('工单模板还在加载，请稍候再试');
      return;
    }
    if (ticketTemplates.length > 0 && !appliedTemplateId) {
      Message.warning('请先选择「应用工单模板」，再点击 AI 预填');
      return;
    }
    setPrefilling(true);
    try {
      const d: PrefillDraft = await prefillTicket(cdr?.id ? 'cdr' : 'chat', cdr?.id || chatSessionId!);
      form.setFieldsValue({
        title: d.title,
        type: d.type,
        priority: d.priority,
        description: d.description || '',
        workflowTemplateId: d.workflow_template_id || undefined,
      });
      // v1.16 AI 预填扩展字段（坐席可改）
      const fv = d.field_values || {};
      const fkeys = Object.keys(fv);
      if (fkeys.length) {
        fkeys.forEach((k) => form.setFieldValue(`df_${k}`, fv[k]));
        Message.success('已预填描述与扩展字段，可修改后提交');
      } else {
        Message.success('已预填描述，可修改后提交');
      }
    } catch { /* 拦截器已提示（失败提示一元化） */ } finally {
      setPrefilling(false);
    }
  };

  const onSubmit = async () => {
    const v = await form.validate();
    // v1.16 扩展字段：收集可见字段的非空值（模板隐藏字段不提交）
    const dv: Record<string, string> = {};
    visibleFields.forEach((f) => {
      const val = v[`df_${f.field_key}`];
      if (val !== undefined && val !== null && val !== '') dv[f.field_key] = String(val);
    });
    setSaving(true);
    try {
      const created: Ticket = await createTicket({
        title: v.title,
        type: v.type,
        priority: v.priority,
        customerId: v.customerId,
        // 通话/会话建单未选中客户时，把号码透传给后端按号查/建客户
        customerPhone: v.customerId ? undefined : contextPhone,
        cdrId: cdr?.id,
        chatSessionId: chatSessionId,
        uuid: uuid,
        followUp: !!v.followUp,
        workflowTemplateId: v.workflowTemplateId,
        // 留空 = 交后端按派单规则自动指派处理人（无人单既不在待办也不在坐席列表里）
        assigneeId: v.assigneeId,
        description: v.description,
        content: v.content,
        dynamicFields: Object.keys(dv).length ? dv : undefined,
      });
      // 工单号当场要念给客户、后面还要写进小结：3 秒 toast 一闪而过看不清也复制不了，
      // 故改用常驻结果弹层，把编号放大 + 一键复制（创建成功即非可编辑表单，不构成裸 Modal 表单）。
      // 口径必须与「工单」主列表一致——列表那列「工单号」显示的字段就是 id。
      // 不要改用 ticket_no：主列表不展示它，用了会让同一张单在弹层与列表出现两个不同编号。
      const ticketNo = created?.id ? String(created.id) : '';
      Modal.success({
        title: '工单已创建',
        okText: '知道了',
        content: (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div>
              工单号：
              {ticketNo ? (
                <CopyText text={ticketNo}>
                  <Typography.Text className="bcc-mono" style={{ fontSize: 16, fontWeight: 600 }}>
                    {ticketNo}
                  </Typography.Text>
                </CopyText>
              ) : (
                <Typography.Text type="secondary">未返回工单号，请到「工单」列表核对</Typography.Text>
              )}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              已关联本通通话；需要审批时，由处理人在工单详情点「提交审批」发起
            </Typography.Text>
          </Space>
        ),
      });
      onClose(created);
    } catch { /* 拦截器已提示 */ } finally {
      setSaving(false);
    }
  };

  const modalTitle = cdr
    ? `为通话 #${cdr.id} 发起工单`
    : chatSessionId
      ? '为在线会话发起工单'
      : uuid
        ? '为当前通话发起工单'
        : '发起工单';

  const remoteNumber = cdr
    ? cdr.direction === 'inbound'
      ? cdr.caller
      : cdr.callee
    : '';

  return (
    <FormModal
      form={form}
      title={modalTitle}
      visible={visible}
      width={FORM_MODAL_WIDTH.wide}
      onOk={onSubmit}
      onCancel={() => onClose()}
      confirmLoading={saving}
    >
      {cdr?.id && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          关联通话 #{cdr.id}（{cdr.direction === 'inbound' ? '呼入' : '呼出'}，对方 {maskPhone(remoteNumber) || '-'}），
          工单关闭勾选「需要回访」将自动生成回访外呼任务。
        </Typography.Text>
      )}
      {!cdr && uuid && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          关联当前进行中的通话（uuid: {uuid}），CDR 落库后自动绑定。
        </Typography.Text>
      )}
      {!cdr && !uuid && chatSessionId && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          关联在线会话 #{chatSessionId}，工单关闭勾选「需要回访」将自动生成回访外呼任务。
        </Typography.Text>
      )}
      {/* 正文限高滚动由 FormModal 的内层主体容器（.bcc-modal-body）单层承担：
       * 此处再套一层会与外层各出一条滚动条，且校验失败自动滚到首个错误项会被内层短路 */}
      <Form form={form} layout="vertical">
        <div className="wb-form-section-title">工单信息</div>
        {ticketTemplates.length > 0 && (
          <Form.Item
            field="appliedTemplate"
            label={<>应用工单模板<FieldTip content="选择模板一键预填并限定动态字段（可再修改）" /></>}
          >
            <Select
              allowClear
              placeholder="选择工单创建模板"
              value={appliedTemplateId}
              onChange={(v) => applyTicketTemplate(ticketTemplates.find((t) => t.id === v))}
              options={ticketTemplates.map((t) => ({ label: t.name, value: t.id }))}
            />
          </Form.Item>
        )}
        {/* AI 预填紧跟模板选择器之后（顺序 = 先定模板、再抽值）：状态提示先说明会发生什么，按钮再跟上 */}
        {(cdr?.id || chatSessionId) && (
          <div style={{ marginTop: -10, marginBottom: 12 }}>
            {ticketTemplates.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                {prefillBlocked ? prefillTip : 'AI 按所选模板的字段抽取，只填空白项'}
              </Typography.Text>
            )}
            {/* 禁用按钮不派发鼠标事件，Tooltip 需挂在包裹层上才弹得出来 */}
            <Tooltip content={prefillBlocked ? prefillTip : ''} position="top">
              <span style={{ display: 'inline-block' }}>
                <Button
                  size="small"
                  icon={<IconThunderbolt />}
                  loading={prefilling}
                  disabled={prefillBlocked}
                  onClick={doPrefill}
                >
                  AI 预填{cdr?.id ? '（从通话转写生成）' : '（从会话消息生成）'}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        <Form.Item field="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
          <Input maxLength={100} placeholder="工单标题" />
        </Form.Item>
        <div className="wb-form-grid2">
          <Form.Item field="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item field="priority" label="优先级" rules={[{ required: true, message: '请选择优先级' }]}>
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
        </div>
        <Form.Item field="workflowTemplateId" label={<>审核流程<FieldTip content="留空＝不走审批，处理人直接办理到关闭；填了则这张单办完后由处理人点「提交审批」发起流程，审批全部通过后工单自动完成" /></>}>
          <Select
            allowClear
            placeholder={workflows.length ? '留空＝免审；选择则办完后提交审批' : '暂无启用中的工单审核流程（免审）'}
            options={workflows.map((w) => ({ label: `${w.name}（${w.code}）`, value: w.id }))}
          />
        </Form.Item>
        <Form.Item field="assigneeId" label={<>处理人<FieldTip content="留空则由派单规则自动指派：规则的默认处理人 → 负责技能组内负载最轻的坐席 → 创建人" /></>}>
          <Select
            showSearch
            allowClear
            options={users.map((u) => ({ label: u.name || u.username, value: u.id }))}
            placeholder="留空自动指派"
          />
        </Form.Item>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          处理人负责办理这张单，可在工单详情继续转派；审核流程负责放行——未选流程即免审，选了则办完后提交审批、全部通过后工单自动完成。
        </Typography.Text>
        <div className="wb-form-section-title">客户与描述</div>
        <Form.Item field="customerId" label="关联客户">
          <Select
            showSearch
            allowClear
            filterOption={false}
            onSearch={searchCustomers}
            options={customers.map((c) => ({
              label: `${c.name || '-'}（${maskPhone(c.phone) || '-'}）`,
              value: c.id,
            }))}
            placeholder="输入姓名/电话搜索"
          />
        </Form.Item>
        <Form.Item field="description" label="描述" rules={[{ required: true, message: '请输入问题描述' }]}>
          <Input.TextArea rows={3} placeholder="详细问题描述（AI 分类依据，请尽量写清客户诉求）" />
        </Form.Item>
        {/* v1.16 扩展字段：统一组件；选了模板按模板所需字段渲染，AI 预填后可修改；无字段自动隐藏 */}
        <DynamicTicketFields form={form} fields={visibleFields} categoryTree={categoryTree} />
        <div className="wb-form-section-title">补充与回访</div>
        <Form.Item field="content" label="补充内容">
          <Input.TextArea rows={2} placeholder="备注 / 处理要求（可选）" />
        </Form.Item>
        <Form.Item field="followUp" label=" " triggerPropName="checked">
          <Space>
            <Switch />
            <span>需要回访（关闭工单后自动生成「工单回访」外呼任务）</span>
          </Space>
        </Form.Item>
      </Form>
    </FormModal>
  );
}
